
import { useState, useRef, useEffect } from "react";

interface IndicatorStyle {
  width?: string;
  left?: string;
  bottom?: string;
  height?: string;
  position?: 'absolute' | 'relative' | 'fixed' | 'sticky';
  backgroundColor?: string;
  display?: string;
  zIndex?: number;
  transition?: string;
}

/**
 * A custom hook to manage tab indicator positioning and animation
 */
export function useTabIndicator(initialTab: string) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [indicatorStyle, setIndicatorStyle] = useState<IndicatorStyle>({});
  const tabsListRef = useRef<HTMLDivElement>(null);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    // Use setTimeout to ensure DOM is updated before measuring
    setTimeout(() => {
      updateIndicator(value);
    }, 150);
  };

  const updateIndicator = (value: string) => {
    if (!tabsListRef.current) return;
    
    const tabsList = tabsListRef.current;
    const activeTabElement = tabsList.querySelector(`[value="${value}"]`);
    
    if (activeTabElement) {
      const tabsListRect = tabsList.getBoundingClientRect();
      const activeTabRect = activeTabElement.getBoundingClientRect();
      
      // Set all required properties explicitly to ensure the indicator is visible
      setIndicatorStyle({
        width: `${activeTabRect.width}px`,
        left: `${activeTabRect.left - tabsListRect.left}px`,
        bottom: '0px',
        height: '3px',
        position: 'absolute',
        backgroundColor: '#F85A00',
        display: 'block',
        zIndex: 20,
        transition: 'none', // First set position without animation
      });
      
      // Then re-enable animation after a short delay
      setTimeout(() => {
        setIndicatorStyle(prev => ({
          ...prev,
          transition: 'all 0.3s ease-in-out',
        }));
      }, 50);
    }
  };

  // Initialize and handle window resize
  useEffect(() => {
    // Add a delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      updateIndicator(activeTab);
    }, 200);
    
    const handleResize = () => {
      updateIndicator(activeTab);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, [activeTab]);

  return {
    activeTab,
    tabsListRef,
    indicatorStyle,
    handleTabChange,
    updateIndicator
  };
}
